<template>
  <v-card v-if="hasData" class="actual-costs">
    <v-card-title class="actual-costs__title" @click="expanded = !expanded">
      <div class="actual-costs__title-row">
        <div>
          Tatsächliche Produktionskosten
          <span class="actual-costs__summary" v-if="!expanded">
            {{ insights.orderCount }} Fulfillments · {{ formatEuro(insights.grandTotal) }}<template v-if="insights.reproductionCount"> · {{ insights.reproductionCount }} Repro.</template>
          </span>
        </div>
        <v-icon>{{ expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
      </div>
      <span v-if="expanded" class="actual-costs__hint">
        beim Fulfillen erfasste Rechnungsdaten der Produzenten (Fulfill-Datum, nicht Bestell-Datum)
      </span>
    </v-card-title>
    <v-expand-transition>
    <v-card-text v-show="expanded">
      <!-- Insight-Zeile -->
      <div class="actual-costs__insights">
        <div class="ac-insight">
          <div class="ac-insight__label">Fulfillments</div>
          <div class="ac-insight__value">{{ insights.orderCount }}</div>
        </div>
        <div class="ac-insight">
          <div class="ac-insight__label">Gesamtkosten</div>
          <div class="ac-insight__value">{{ formatEuro(insights.grandTotal) }}</div>
        </div>
        <div class="ac-insight">
          <div class="ac-insight__label">Extras / Rahmen</div>
          <div class="ac-insight__value">
            {{ formatEuro(insights.extrasSum) }}
            <span class="ac-insight__sub">{{ insights.extrasOrders }} Orders</span>
          </div>
        </div>
        <div class="ac-insight" :class="{ 'ac-insight--warn': insights.reproductionCount > 0 }">
          <div class="ac-insight__label">Reproduktionen</div>
          <div class="ac-insight__value">
            {{ insights.reproductionCount }}
            <span v-if="insights.reproductionCosts" class="ac-insight__sub">
              +{{ formatEuro(insights.reproductionCosts) }} Mehrkosten
            </span>
          </div>
        </div>
      </div>

      <!-- Fulfiller-Breakdown -->
      <v-simple-table class="ac-table" dense>
        <thead>
          <tr>
            <th class="text-left">Fulfiller</th>
            <th class="text-right">Fulfillments</th>
            <th class="text-right">Produktion</th>
            <th class="text-right">Versand</th>
            <th class="text-right">Extras</th>
            <th class="text-right">Gesamt</th>
            <th class="text-left pl-6">Produkte</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in fulfillerRows" :key="row.fulfiller">
            <td class="ac-table__name">{{ row.fulfiller }}</td>
            <td class="text-right num">{{ row.orderCount }}</td>
            <td class="text-right num">{{ formatEuro(row.production) }}</td>
            <td class="text-right num">{{ formatEuro(row.shipping) }}</td>
            <td class="text-right num">{{ row.extras ? formatEuro(row.extras) : "—" }}</td>
            <td class="text-right num num--strong">{{ formatEuro(row.total) }}</td>
            <td class="pl-6">
              <span
                v-for="variant in visibleVariants(row)"
                :key="variant.name"
                class="ac-chip"
              >
                {{ variant.name }} <strong>×{{ variant.count }}</strong>
              </span>
              <button
                v-if="row.moreVariants"
                type="button"
                class="ac-chip ac-chip--muted ac-chip--btn"
                @click="toggleVariants(row.fulfiller)"
              >
                {{ expandedVariants.includes(row.fulfiller) ? "weniger" : `+${row.moreVariants} weitere` }}
              </button>
            </td>
          </tr>
          <tr class="ac-table__total">
            <td>Gesamt</td>
            <td class="text-right num">{{ insights.orderCount }}</td>
            <td class="text-right num">{{ formatEuro(insights.productionSum) }}</td>
            <td class="text-right num">{{ formatEuro(insights.shippingSum) }}</td>
            <td class="text-right num">{{ formatEuro(insights.extrasSum) }}</td>
            <td class="text-right num num--strong">{{ formatEuro(insights.grandTotal) }}</td>
            <td />
          </tr>
        </tbody>
      </v-simple-table>

      <!-- Reproduktions-Details (nur wenn vorhanden) -->
      <div v-if="reproductions.length" class="ac-repro">
        <div class="ac-repro__title">
          <v-icon x-small color="#a96617">mdi-restore-alert</v-icon>
          Mehrfach produzierte Tasks
        </div>
        <span v-for="repro in reproductions" :key="repro.taskId" class="ac-chip ac-chip--warn">
          {{ repro.orderName }} · {{ repro.variantName }} ×{{ repro.count }}
        </span>
      </div>

      <!-- Einzel-Orders (aufklappbar wie bisher) -->
      <v-expansion-panels flat class="ac-orders">
        <v-expansion-panel>
          <v-expansion-panel-header class="ac-orders__header">
            Einzelne Fulfillments durchsuchen
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <FulfilledOrderList :data="data" />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
    </v-expand-transition>
  </v-card>
</template>

<script>
import FulfilledOrderList from "@/components/Settings/Statistics/ActualCosts/ProductionCosts/FulfilledOrderList.vue";
import { getDecimalNumberString } from "@/utils";

export default {
  name: "ActualCosts",
  components: { FulfilledOrderList },
  props: {
    data: {
      type: Object
    }
  },
  data() {
    return {
      expanded: false,
      expandedVariants: [],
    };
  },
  computed: {
    hasData() {
      return Object.keys(this.data?.actualPaid || {}).length > 0;
    },
    /** Alle Fulfillment-Einträge flach: {fulfiller, order} */
    allEntries() {
      const entries = [];
      for (const [fulfiller, byShop] of Object.entries(this.data?.actualPaid || {})) {
        for (const byCountry of Object.values(byShop || {})) {
          for (const region of Object.values(byCountry || {})) {
            for (const order of region?.orders || []) {
              entries.push({ fulfiller, order });
            }
          }
        }
      }
      return entries;
    },
    fulfillerRows() {
      const byFulfiller = {};
      for (const { fulfiller, order } of this.allEntries) {
        if (!byFulfiller[fulfiller]) {
          byFulfiller[fulfiller] = {
            fulfiller, orderCount: 0, production: 0, shipping: 0, extras: 0, variants: {},
          };
        }
        const row = byFulfiller[fulfiller];
        row.orderCount += 1;
        row.production += Number(order.productionCosts) || 0;
        row.shipping += Number(order.shippingCost) || 0;
        row.extras += Number(order.costsExtras) || 0;
        for (const task of order.fulfilledTasks || []) {
          const name = task.variantName || "Unbekannt";
          row.variants[name] = (row.variants[name] || 0) + 1;
        }
        // Eigenständige Extras (kein Ersatz-Artikel — die stecken schon im
        // Varianten-Namen) als eigene Produkt-Chips zählen
        for (const extra of order.extras || []) {
          if (extra.isReplace) continue;
          const name = extra.name || extra.sku || "Extra";
          row.variants[name] = (row.variants[name] || 0) + (extra.quantity || 1);
        }
      }
      return Object.values(byFulfiller)
        .map((row) => {
          const sorted = Object.entries(row.variants)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count);
          return {
            ...row,
            total: row.production + row.shipping,
            allVariants: sorted,
            topVariants: sorted.slice(0, 4),
            moreVariants: Math.max(0, sorted.length - 4),
          };
        })
        .sort((a, b) => b.total - a.total);
    },
    /** Tasks, die im Zeitraum mehrfach produziert wurden (Reproduktionen) */
    reproductions() {
      const byTask = {};
      for (const { order } of this.allEntries) {
        for (const task of order.fulfilledTasks || []) {
          if (!task.id) continue;
          if (!byTask[task.id]) {
            byTask[task.id] = { taskId: task.id, count: 0, extraCosts: 0, orderName: order.orderName, variantName: task.variantName || "?" };
          }
          byTask[task.id].count += 1;
          if (byTask[task.id].count > 1) {
            byTask[task.id].extraCosts += Number(task.costs) || 0;
          }
        }
      }
      return Object.values(byTask).filter((task) => task.count > 1);
    },
    insights() {
      const rows = this.fulfillerRows;
      const sum = (key) => rows.reduce((total, row) => total + row[key], 0);
      return {
        orderCount: sum("orderCount"),
        productionSum: sum("production"),
        shippingSum: sum("shipping"),
        extrasSum: sum("extras"),
        grandTotal: sum("production") + sum("shipping"),
        extrasOrders: this.allEntries.filter(({ order }) => (order.costsExtras || 0) > 0).length,
        reproductionCount: this.reproductions.length,
        reproductionCosts: this.reproductions.reduce((total, repro) => total + repro.extraCosts, 0),
      };
    },
  },
  methods: {
    visibleVariants(row) {
      return this.expandedVariants.includes(row.fulfiller) ? row.allVariants : row.topVariants;
    },
    toggleVariants(fulfiller) {
      this.expandedVariants = this.expandedVariants.includes(fulfiller)
        ? this.expandedVariants.filter((f) => f !== fulfiller)
        : [...this.expandedVariants, fulfiller];
    },
    formatEuro(value) {
      return `${getDecimalNumberString(value || 0, 2)} €`;
    },
  },
};
</script>

<style scoped lang="scss">
.actual-costs {
  margin-top: 12px;

  &__title {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding-bottom: 12px;
    cursor: pointer;
    user-select: none;
  }

  &__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &__summary {
    font-size: 12px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.45);
    margin-left: 10px;
  }

  &__hint {
    font-size: 11px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.4);
  }

  &__insights {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 10px;
    margin-bottom: 14px;
  }
}

.ac-insight {
  background: #f6f8fa;
  border-radius: 10px;
  padding: 10px 14px;

  &--warn {
    background: #fdf4e3;
  }

  &__label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(0, 0, 0, 0.45);
  }

  &__value {
    font-size: 18px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  &__sub {
    font-size: 11.5px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.45);
    margin-left: 4px;
  }
}

.ac-table {
  background: transparent !important;

  th {
    font-size: 11px !important;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(0, 0, 0, 0.45) !important;
    white-space: nowrap;
  }

  &__name {
    font-weight: 700;
    font-size: 13px;
    text-transform: capitalize;
  }

  &__total td {
    border-top: 2px solid rgba(0, 0, 0, 0.12);
    font-weight: 700;
  }
}

.num {
  font-variant-numeric: tabular-nums;
  font-size: 12.5px;
  white-space: nowrap;

  &--strong {
    font-weight: 700;
  }
}

.ac-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #eef0f3;
  border-radius: 999px;
  font-size: 11px;
  padding: 1px 9px;
  margin: 2px 3px 2px 0;
  white-space: nowrap;

  &--muted {
    background: transparent;
    border: 1px dashed rgba(0, 0, 0, 0.2);
    color: rgba(0, 0, 0, 0.5);
  }

  &--warn {
    background: #fdf4e3;
    color: #a96617;
    font-weight: 600;
  }

  &--btn {
    cursor: pointer;

    &:hover {
      border-style: solid;
      color: rgba(0, 0, 0, 0.75);
    }
  }
}

.ac-repro {
  margin-top: 12px;

  &__title {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 700;
    color: #a96617;
    margin-bottom: 4px;
  }
}

.ac-orders {
  margin-top: 10px;

  &__header {
    font-size: 13px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.6);
    padding-left: 0;
  }
}
</style>
